import { twMerge } from "tailwind-merge";
import { Button, type Props as ButtonProps } from "@themes/pollinator/ui/Button";
import { ThemeType } from "@core/types/style.types";

export type Props = {
  /** Theme variant */
  theme?: ThemeType;
  /** Secondary button click handler */
  onSecondaryClick?: (() => Promise<void>) | (() => void);
  /** Primary button click handler */
  onPrimaryClick?: (() => Promise<void>) | (() => void);
  /** Secondary button text */
  secondaryButtonText?: string;
  /** Primary button text */
  primaryButtonText?: string;
  /** Whether buttons should be full width */
  isButtonsFullWidth?: boolean;
  /** Additional props for primary button */
  primaryButtonProps?: ButtonProps;
  /** Additional props for secondary button */
  secondaryButtonProps?: ButtonProps;
  /** Additional CSS classes */
  className?: string;
};

/**
 * A group of action buttons, typically used for form actions or dialogs.
 * Renders primary and/or secondary buttons based on provided props.
 */
export const ActionButtonsGroup = ({
  theme = "light",
  onSecondaryClick,
  onPrimaryClick,
  secondaryButtonText,
  primaryButtonText,
  secondaryButtonProps,
  primaryButtonProps,
  className,
  isButtonsFullWidth = false,
}: Props) => {
  const isWithSecondaryButton = onSecondaryClick && secondaryButtonText;
  const isWithPrimaryButton = onPrimaryClick && primaryButtonText;
  const isWithButtons = isWithSecondaryButton || isWithPrimaryButton;

  if (!isWithButtons) return null;

  return (
    <div
      className={twMerge(
        "flex items-start gap-2 md:items-end",
        className
      )}
      data-testid="action-buttons-group"
    >
      {isWithSecondaryButton && (
        <Button
          variant="secondary"
          theme={theme}
          onClick={onSecondaryClick}
          data-testid="action-buttons-group-secondary"
          className={twMerge(
            "min-w-24 md:size-max",
            isButtonsFullWidth && "w-full md:w-full",
            secondaryButtonProps?.className
          )}
          {...secondaryButtonProps}
        >
          {secondaryButtonText}
        </Button>
      )}
      {isWithPrimaryButton && (
        <Button
          theme={theme}
          onClick={onPrimaryClick}
          data-testid="action-buttons-group-primary"
          className={twMerge(
            "min-w-24 md:size-max",
            isButtonsFullWidth && "w-full md:w-full",
            primaryButtonProps?.className
          )}
          {...primaryButtonProps}
        >
          {primaryButtonText}
        </Button>
      )}
    </div>
  );
};

export default ActionButtonsGroup;
